import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import ProcoreSendToDocumentsCard from '../components/ProcoreSendToDocuments';
import { useProject } from '../ProjectContext';
import { getProjectName } from '@shared/targetHelpers';
import { agentsForDocument, isSystemAgent } from '@shared/agentMatching';
import {
  fetchDocument,
  fetchBlobObjectUrl,
  downloadBlob,
  submitAiCorrection,
  fetchAgents,
  fetchAiConnections,
  runAgentOnDocument,
  runAiForAttachment,
  pushDocumentToProcore,
  fetchIntegrationStatus,
  reindexDocument,
} from '../services/api';
import { Button, Card, Select, Loading, ErrorText, errorMessage } from '../components/ui';

// Drawing comparison and photo analysis are separate workers with different
// names for the same ideas (photo analysis writes followUpActions, not
// actions), so each field checks its aliases too.
const FIELD_DEFS = [
  { key: 'summary', label: 'Summary', type: 'text' },
  { key: 'drawingNumber', label: 'Drawing number', type: 'text' },
  { key: 'revision', label: 'Revision', type: 'text' },
  { key: 'drawingTitle', label: 'Drawing title', type: 'text' },
  { key: 'observations', label: 'Observations', type: 'list' },
  { key: 'risks', label: 'Risks', type: 'list', aliases: ['qualityRisks', 'programmeRisks'] },
  { key: 'actions', label: 'Recommended actions', type: 'list', aliases: ['followUpActions', 'recommendedActions'] },
  { key: 'changes', label: 'Changes', type: 'list' },
];

// An agent given reference standards returns standardsAssessment: one entry per
// finding, each carrying the clause it rests on. It is deliberately NOT in
// FIELD_DEFS - those are flat strings and lists, edited as text and flattened
// into the Procore description, and an array of objects rendered through that
// path is not just wrong but fatal (React throws on an object as a child).
//
// The clause is shown in full rather than truncated. The whole point of the
// citation is that someone can check the finding against the words of the
// standard without opening the manual.
// The plain-language requirement and the quoted clause are meant to be
// different things, and the prompt says so - but a model handed both fields
// will sometimes put the same sentence in each, and printing it twice makes
// the card look broken. When one contains the other, only the clause is shown:
// it is the authoritative wording and the reason the citation exists.
function saysTheSame(a = '', b = '') {
  const norm = (s) => String(s).toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  const [x, y] = [norm(a), norm(b)];
  if (!x || !y) return false;
  return x.includes(y) || y.includes(x);
}

function StandardsAssessment({ entries, identifiedAs }) {
  const list = Array.isArray(entries) ? entries.filter((e) => e && typeof e === 'object') : [];
  if (!list.length) return null;
  return (
    <div className="field-block">
      <div className="field-name">Assessed against standards</div>
      {/* First, because it is the one thing nothing downstream can check. Get
          the form of construction wrong and every clause below is real,
          correctly cited, and about something else. */}
      {identifiedAs
        ? (
          <div className="field-value" style={{ marginBottom: 12 }}>
            <span className="muted">Taken to be: </span>{String(identifiedAs)}
            <div className="sub" style={{ fontSize: 12.5, marginTop: 2 }}>
              Everything below depends on this. If it is wrong, the findings are too.
            </div>
          </div>
        )
        : null}
      {list.map((entry, i) => (
        <div key={i} style={{ marginBottom: 14 }}>
          <div className="field-value">
            <strong>{String(entry.verdict || 'assessed')}</strong>
            {entry.citation ? <span className="muted"> · {String(entry.citation)}</span> : null}
          </div>
          {/* `finding` is the older shape - results indexed before the schema
              split it into requirement/observed still carry it. */}
          {(entry.requirement || entry.finding) && !saysTheSame(entry.requirement || entry.finding, entry.clause)
            ? <div className="field-value">{String(entry.requirement || entry.finding)}</div>
            : null}
          {entry.observed ? <div className="field-value"><span className="muted">Seen: </span>{String(entry.observed)}</div> : null}
          {entry.checkOnSite ? <div className="field-value"><span className="muted">Check on site: </span>{String(entry.checkOnSite)}</div> : null}
          {entry.clause
            ? <blockquote className="sub" style={{ margin: '4px 0 0', paddingLeft: 10, borderLeft: '3px solid currentColor', opacity: 0.75 }}>{String(entry.clause)}</blockquote>
            : null}
          {/* Stamped by the API from the passage the quote was actually found
              in, not written by the model - so it can be opened and checked.
              A document short enough never to be chunked has no page range,
              and then the source alone is the whole citation. */}
          {entry.source
            ? (
              <div className="field-value" style={{ marginTop: 4, fontSize: 13, opacity: 0.8 }}>
                {String(entry.source)}{entry.page ? ` — ${String(entry.page)}` : ''}
              </div>
            )
            : null}
        </div>
      ))}
    </div>
  );
}

function readField(result, def) {
  const value = result?.[def.key];
  if (Array.isArray(value) ? value.length : value) return value;
  for (const alias of def.aliases || []) {
    const aliasValue = result?.[alias];
    if (Array.isArray(aliasValue) ? aliasValue.length : aliasValue) return aliasValue;
  }
  return value;
}

// The AI analysis as readable text for a Procore photo description.
//
// Built from the same FIELD_DEFS the page renders, so what lands in Procore
// is what the reader saw here - including any correction they saved, since
// the API overlays corrections onto doc.ai before it ever reaches this page.
//
// Text rather than the raw object: buildProcoreDescription JSON.stringifies
// anything that isn't a string, and a wall of JSON in a photo description is
// not what anyone opening Procore wants to read.
function procoreAiAnalysis(doc) {
  const blocks = [];
  const add = (label, result) => {
    if (!result) return;
    const lines = [];
    for (const def of FIELD_DEFS) {
      const value = readField(result, def);
      if (Array.isArray(value) ? !value.length : !value) continue;
      lines.push(Array.isArray(value)
        ? `${def.label}:\n${value.map((v) => `  - ${v}`).join('\n')}`
        : `${def.label}: ${value}`);
    }
    if (lines.length) blocks.push(label ? `${label}\n${lines.join('\n')}` : lines.join('\n'));
  };

  add('', { summary: doc?.ai?.summary, ...(doc?.ai?.result || {}) });
  // Each agent that ran adds its own block, named, so several agents on one
  // photo stay tellable apart in Procore.
  for (const run of Array.isArray(doc?.agentResults) ? doc.agentResults : []) {
    add(run?.agentName || run?.agentId || 'Agent', run?.result || run);
  }
  return blocks.join('\n\n');
}

const toDisplay = (v) => (Array.isArray(v) ? v.join('\n') : v == null ? '' : String(v));
const fromEdit = (type, text) => (type === 'list' ? text.split('\n').map((s) => s.trim()).filter(Boolean) : text.trim());
const isImage = (doc) => String(doc?.contentType || '').toLowerCase().startsWith('image/') || /\.(png|jpe?g|webp|heic|heif)$/i.test(String(doc?.fileName || ''));

// Which Procore resource a document belongs in.
//
// A table rather than a chain of ternaries, because this list is going to grow
// - RFIs and meeting notes are next - and a two-way ternary is exactly what
// produced the bug this replaces: everything that was not a photo claimed to
// be a drawing, and went to the drawing register.
//
// A drawing is the Drawings upload type and nothing else. Deliberately an
// exact match rather than anything cleverer: the upload type is the one place
// a person has already said what a file is, and second-guessing it is how
// things end up in the wrong register. A type renamed away from "Drawings"
// falls to document, which is the safe direction.
//
// 'document' is the default on purpose. An unknown type going to the Documents
// tool is filed somewhere a person can find it; an unknown type going to the
// drawing register is a mess someone else has to clean up.
const PROCORE_KINDS = [
  { kind: 'photo', matches: (doc) => isImage(doc) },
  { kind: 'drawing', matches: (doc) => String(doc?.uploadType || '').trim().toLowerCase() === 'drawings' },
];

export function procoreKindOf(doc) {
  for (const { kind, matches } of PROCORE_KINDS) {
    if (matches(doc)) return kind;
  }
  return 'document';
}
const isPdf = (doc) => String(doc?.contentType || '').toLowerCase().includes('pdf') || String(doc?.fileName || '').toLowerCase().endsWith('.pdf');

function formatDate(value) {
  if (!value) return '';
  try { return new Date(value).toLocaleString(); } catch { return String(value); }
}

// One AI response: the base analysis, or one agent's. Both get the same
// read/edit/correct treatment, so a correction is available on an agent's
// output exactly as on the original.
function AiResponseCard({ title, subtitle, status, result, onSaveCorrection, emptyText, pending }) {
  const [editing, setEditing] = useState(false);
  const [fields, setFields] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState('');

  // A standards assessment counts as analysis: without this, a result whose
  // only content is its citations would still say "no analysis yet".
  const hasStandards = Array.isArray(result?.standardsAssessment) && result.standardsAssessment.length > 0;

  const visible = useMemo(
    () => FIELD_DEFS.filter((def) => {
      const value = readField(result, def);
      return Array.isArray(value) ? value.length > 0 : Boolean(value);
    }),
    [result],
  );

  function startEditing() {
    const initial = {};
    for (const def of FIELD_DEFS) initial[def.key] = toDisplay(readField(result, def));
    setFields(initial);
    setError('');
    setSaved('');
    setEditing(true);
  }

  async function handleSave() {
    setSaving(true);
    setError('');
    try {
      const corrected = {};
      for (const def of FIELD_DEFS) {
        const value = fromEdit(def.type, fields[def.key] || '');
        corrected[def.key] = value;
        for (const alias of def.aliases || []) corrected[alias] = value;
      }
      await onSaveCorrection(corrected);
      setEditing(false);
      setSaved('Correction saved.');
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card title={title} subtitle={subtitle} badge={status}>
      {!editing ? (
        <>
          {visible.length ? visible.map((def) => {
            const value = readField(result, def);
            return (
              <div className="field-block" key={def.key}>
                <div className="field-name">{def.label}</div>
                {Array.isArray(value)
                  ? <ul className="field-list">{value.map((line, i) => <li key={i}>{line}</li>)}</ul>
                  : <div className="field-value">{value}</div>}
              </div>
            );
          }) : (!hasStandards && <p className="muted">{pending ? 'Analysis is still running - the result appears here when it finishes.' : emptyText}</p>)}
          <StandardsAssessment entries={result?.standardsAssessment} identifiedAs={result?.identifiedAs} />
        </>
      ) : (
        FIELD_DEFS.map((def) => (
          <div className="field-block" key={def.key}>
            <div className="field-name">{def.label}{def.type === 'list' ? ' (one per line)' : ''}</div>
            {def.type === 'list'
              ? <textarea className="input" value={fields[def.key] || ''} onChange={(e) => setFields((p) => ({ ...p, [def.key]: e.target.value }))} />
              : <input className="input" value={fields[def.key] || ''} onChange={(e) => setFields((p) => ({ ...p, [def.key]: e.target.value }))} />}
          </div>
        ))
      )}

      {saved && <p className="saved">{saved}</p>}
      <ErrorText>{error}</ErrorText>

      {editing ? (
        <div className="row">
          <Button compact onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save correction'}</Button>
          <Button compact secondary onClick={() => setEditing(false)} disabled={saving}>Cancel</Button>
        </div>
      ) : (
        <Button secondary compact onClick={startEditing}>Edit response</Button>
      )}
    </Card>
  );
}

// The server reports only runs it knows about, and the requeue endpoint writes
// no marker into state - so a locally-queued run must survive polls until its
// result lands, or the row vanishes while the agent is still working.
const LOCAL_RUN_TIMEOUT_MS = 10 * 60 * 1000;

function mergeRuns(prev, fresh) {
  const serverRuns = Array.isArray(fresh.agentRuns) ? fresh.agentRuns : [];
  const results = Array.isArray(fresh.agentResults) ? fresh.agentResults : [];
  const finished = new Set(results.map((r) => String(r.agentId || r.agentName || '').toLowerCase()));
  const now = Date.now();
  const local = (Array.isArray(prev.agentRuns) ? prev.agentRuns : []).filter((run) => {
    if (!run?.local) return false;
    const key = String(run.agentId || run.agentName || '').toLowerCase();
    if (!key || finished.has(key)) return false;
    if (serverRuns.some((s) => String(s.agentId || s.agentName || '').toLowerCase() === key)) return false;
    return now - new Date(run.queuedAt || 0).getTime() < LOCAL_RUN_TIMEOUT_MS;
  });
  return [...serverRuns, ...local];
}

// Sends a document that is already in this tenant's storage across to Procore.
// The file is never re-uploaded from the browser - only its blob name goes to
// the API, which streams it from storage to Procore directly.
//
// Shown only when Procore is actually connected AND projects have been
// imported. Both conditions matter: the imported project list lives in tenant
// state and outlives a disconnect, so projects alone would keep offering a
// button that can no longer authenticate. The live connection status comes
// from the connection store, which is what disconnecting actually clears.
// Only for a project that came FROM Procore, and only to the Procore project
// it came from. There is nothing to choose: a dropdown of every Procore
// project invited someone to file a photo against a job it has nothing to do
// with, and got in the way on every upload for the one case that is right. A
// project created by hand has no Procore counterpart, so the card is absent.
function ProcorePushCard({ doc, project }) {
  const [connected, setConnected] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState('');
  const [error, setError] = useState('');

  const procoreProjectId = String(project?.procoreProjectId || '');

  useEffect(() => {
    // Nothing to check for a project that isn't from Procore - and no request
    // made for it either.
    if (!procoreProjectId) return undefined;
    let live = true;
    fetchIntegrationStatus()
      .then((status) => {
        const procore = (status?.providers || []).find((p) => p.id === 'procore');
        if (live) setConnected(Boolean(procore?.connected));
      })
      .catch(() => { if (live) setConnected(false); });
    return () => { live = false; };
  }, [procoreProjectId]);

  if (!procoreProjectId || !connected) return null;

  // Photos and drawings are different Procore resources with different
  // endpoints, so the document's own type decides which one it goes to.
  //
  // This was `isImage(doc) ? 'photo' : 'drawing'` - a two-way split with no
  // third case, so EVERYTHING that was not an image claimed to be a drawing.
  // Not merely a mislabel: kind is sent to the API and picks the destination,
  // so a rate schedule or an order acknowledgement was pushed into the
  // customer's drawing register. Silently, and with the card cheerfully
  // reporting "Sent to Procore as a drawing."
  const kind = procoreKindOf(doc);

  // Documents have their own card below, because the Documents tool needs a
  // folder chosen and these two endpoints do not take one. So this card simply
  // steps aside rather than offering a wrong destination.
  if (kind === 'document') return null;

  async function send() {
    setBusy(true);
    setError('');
    setResult('');
    try {
      const data = await pushDocumentToProcore({
        procoreProjectId,
        blobName: doc.blobName,
        fileName: doc.fileName,
        kind,
        // Matches what the Hub sent (Programme's pushPhotoToProcore). The
        // API's buildProcoreDescription has always read uploadedBy /
        // uploadedAt / tags / comment / aiImageAnalysis - this client just
        // never sent them, so every photo arrived in Procore with an empty
        // description.
        metadata: {
          // The full tier path, not just the two tiers Procore's own location
          // field can hold - "Block A / Level 2 / Zone 3" reads as one place,
          // and the deeper tiers have nowhere else to go.
          location: Array.isArray(doc.tiers) ? doc.tiers : [doc.block, doc.level].filter(Boolean),
          package: doc.package || '',
          uploadType: doc.uploadType || '',
          uploadedBy: doc.uploadedBy || doc.uploadedByName || doc.userEmail || '',
          uploadedAt: doc.uploadedAt || '',
          // documentTags, NOT tags. A document carries both: documentTags is
          // the user's tag list (Record, Evidence, Issue...), while `tags` is
          // the raw blob index tag record - an object of project/supplier/
          // block/level. Sending doc.tags failed the isArray check silently
          // and every photo reached Procore with no tags at all.
          tags: Array.isArray(doc.documentTags) ? doc.documentTags : [],
          comment: doc.comment || doc.description || '',
          aiImageAnalysis: procoreAiAnalysis(doc),
          block: doc.block || '',
          level: doc.level || '',
          drawingNumber: doc.ai?.result?.drawingNumber || '',
          revision: doc.ai?.result?.revision || '',
          title: doc.ai?.result?.drawingTitle || doc.ai?.summary || '',
        },
      });
      setResult(data?.warning || `Sent to Procore as a ${kind}.`);
    } catch (err) { setError(errorMessage(err)); } finally { setBusy(false); }
  }

  return (
    <Card
      title="Send to Procore"
      hint={`This ${kind} will be added to “${getProjectName(project)}” in Procore.`}
    >
      {/* Where it came from, before anything about sending it. A document
          imported from Procore and then pushed back is the round trip this
          app exists to do, and on the one screen where somebody checks a
          single document, both halves should be readable. */}
      {doc.procore?.importedAt || doc.procore?.fileId ? (
        <p className="card-hint" style={{ marginTop: 0 }}>
          Imported from Procore
          {doc.procore.importedAt ? ` ${formatDate(doc.procore.importedAt)}` : ''}
          {doc.procore.folderPath ? ` · ${doc.procore.folderPath}` : ''}.
        </p>
      ) : null}
      {/* Says so when it has already gone. Sending twice creates a second
          copy in Procore rather than replacing the first, so the button
          stays available but stops being the obvious thing to press. */}
      {doc.procore?.pushedAt ? (
        <p className="saved">Already sent {formatDate(doc.procore.pushedAt)}.</p>
      ) : null}
      <ErrorText>{error}</ErrorText>
      {result ? <p className="saved">{result}</p> : null}
      <Button compact secondary={Boolean(doc.procore?.pushedAt)} onClick={send} disabled={busy}>
        {busy ? 'Sending…' : doc.procore?.pushedAt ? `Send ${kind} again` : `Send ${kind} to Procore`}
      </Button>
    </Card>
  );
}

export default function DocumentDetailPage() {
  const { projectId, blobName } = useParams();
  // ?page=N from a chat citation. Chrome's and Firefox's PDF viewers both
  // honour a #page=N fragment, including on the blob URL the preview uses,
  // so a citation can land on the page its passage came from rather than at
  // the front of a 47-page datasheet.
  const [searchParams] = useSearchParams();
  const wantedPage = Number.parseInt(searchParams.get('page') || '', 10);
  const cameFromChat = searchParams.get('from') === 'chat';
  const { project } = useProject();
  const projectName = getProjectName(project);

  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [previewUrl, setPreviewUrl] = useState('');
  // Repairing an unsearchable document, in place. Kept apart from `error`
  // because this one reports on an action the reader just took, and `error`
  // is cleared by every reload of the document.
  const [reindexing, setReindexing] = useState(false);
  const [reindexNote, setReindexNote] = useState(null);
  const [agents, setAgents] = useState([]);
  // "No agents are set up for this" and "the agent list hasn't arrived
  // yet" are different states that both leave `agents` empty. Reporting the
  // first while the second is true told people nothing was configured when
  // the agents were simply still on their way.
  const [agentsLoaded, setAgentsLoaded] = useState(false);
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [running, setRunning] = useState(false);
  const [agentError, setAgentError] = useState('');
  const [agentNotice, setAgentNotice] = useState('');
  // Whether this organisation has an AI provider connected at all. An agent
  // cannot run without one, and since there is no default key the answer is
  // "no" for every customer until they set one up - so their first run would
  // otherwise queue, fail somewhere they cannot see, and come back blank.
  // null means "not asked yet", which must not be reported as "none".
  const [hasAiConnection, setHasAiConnection] = useState(null);

  useEffect(() => {
    let live = true;
    fetchAiConnections()
      .then((list) => { if (live) setHasAiConnection(Array.isArray(list) && list.some((c) => c.hasKey)); })
      // A failed lookup must not claim there is no connection - that would put
      // a false blocker in front of someone whose setup is fine.
      .catch(() => { if (live) setHasAiConnection(true); });
    return () => { live = false; };
  }, []);

  const decodedBlobName = decodeURIComponent(blobName || '');

  useEffect(() => {
    if (!projectName) return;
    setLoading(true);
    fetchDocument({ projectName, blobName: decodedBlobName })
      .then(setDoc)
      .catch((err) => setError(errorMessage(err)))
      .finally(() => setLoading(false));
  }, [projectName, decodedBlobName]);

  useEffect(() => {
    fetchAgents().then(setAgents).catch(() => {}).finally(() => setAgentsLoaded(true));
  }, []);

  useEffect(() => {
    if (!doc?.blobName || (!isImage(doc) && !isPdf(doc))) return undefined;
    let revoked = false;
    let current = '';
    fetchBlobObjectUrl(doc.blobName).then((url) => {
      if (revoked) { URL.revokeObjectURL(url); return; }
      current = url;
      setPreviewUrl(url);
    }).catch(() => {});
    return () => { revoked = true; if (current) URL.revokeObjectURL(current); };
  }, [doc?.blobName]);

  const agentResults = Array.isArray(doc?.agentResults) ? doc.agentResults : [];
  const pendingRuns = Array.isArray(doc?.agentRuns) ? doc.agentRuns : [];
  const runnableAgents = useMemo(() => agentsForDocument(agents, doc || {}), [agents, doc]);

  useEffect(() => {
    if (selectedAgentId && !runnableAgents.some((a) => a.id === selectedAgentId)) setSelectedAgentId('');
  }, [runnableAgents, selectedAgentId]);

  // Agents run on a queue, so results arrive seconds-to-minutes later. Poll
  // only while something is outstanding so an idle page isn't hitting the API.
  useEffect(() => {
    if (!pendingRuns.length || !doc?.blobName || !projectName) return undefined;
    let cancelled = false;
    const timer = setInterval(async () => {
      try {
        const fresh = await fetchDocument({ projectName, blobName: doc.blobName });
        if (!cancelled && fresh) setDoc((prev) => ({ ...prev, ...fresh, agentRuns: mergeRuns(prev, fresh) }));
      } catch { /* transient; the next tick retries */ }
    }, 6000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [pendingRuns.length, doc?.blobName, projectName]);

  async function handleRunAgent() {
    if (!selectedAgentId) return;
    setRunning(true);
    setAgentError('');
    setAgentNotice('');
    try {
      const agent = runnableAgents.find((a) => a.id === selectedAgentId);
      if (isSystemAgent(agent || {})) {
        // Built-in agents aren't in the custom agent store, so requeue can't
        // resolve them - they go through the system analysis path instead.
        await runAiForAttachment(
          { blobName: doc.blobName, fileName: doc.fileName, contentType: doc.contentType },
          { projectName, uploadType: doc.uploadType || '', packageName: doc.package || '', supplier: doc.supplier || '', item: doc.item || '' },
        );
      } else {
        // The route already carries the real project id (useParams above);
        // passing the NAME here stamped the name into the index's projectId
        // field, so this document's analysis no longer matched its own project
        // when chat filtered by id, and was dropped before ranking.
        await runAgentOnDocument({ agentId: selectedAgentId, doc, projectId: projectId || projectName, projectName });
      }
      setDoc((prev) => ({
        ...prev,
        agentRuns: [
          ...(Array.isArray(prev.agentRuns) ? prev.agentRuns.filter((r) => r.agentId !== selectedAgentId) : []),
          { agentId: selectedAgentId, agentName: agent?.label || selectedAgentId, status: 'queued', queuedAt: new Date().toISOString(), local: true },
        ],
      }));
      setAgentNotice(`${agent?.label || 'Agent'} queued — its response appears below when it finishes.`);
    } catch (err) {
      setAgentError(errorMessage(err));
    } finally {
      setRunning(false);
    }
  }

  // Read the stored file again and rewrite its index record.
  //
  // Lifted out of the "not searchable" banner because repairing a broken
  // index is not the only reason to want it. The extractor keeps improving -
  // it learned to read PDF comments and their dates this week - and a
  // document indexed before that improvement is perfectly searchable and
  // perfectly stale: its text is in the index, the comments written on it are
  // not. Nothing about such a document looks wrong, which is exactly the
  // problem, and the only fix on offer was to upload the same file again and
  // live with the duplicate.
  async function handleReindex() {
    setReindexing(true);
    setReindexNote(null);
    try {
      const out = await reindexDocument(doc.blobName);
      const done = Number(out?.counts?.indexed) > 0;
      if (done) {
        // Re-read the document so the banner and the badge go on the evidence
        // rather than on this button's say-so. The server records the outcome
        // back onto the blob, and `searchable` is derived from it - so a fetch
        // is the only thing that proves the repair held. Without it the page
        // kept saying "not searchable" after a successful repair, which made a
        // working button look broken.
        const fresh = await fetchDocument({ projectName, blobName: decodedBlobName }).catch(() => null);
        if (fresh) setDoc((prev) => ({ ...prev, ...fresh }));
      }
      const chars = Number(out?.results?.[0]?.chars) || 0;
      setReindexNote(done
        ? { ok: true, text: `Re-indexed${chars ? `, ${chars.toLocaleString()} characters read` : ''}. It will answer questions now.` }
        : { ok: false, text: `Could not re-index it: ${out?.results?.[0]?.reason || 'no text could be extracted'}.` });
    } catch (err) {
      setReindexNote({ ok: false, text: errorMessage(err) });
    } finally {
      setReindexing(false);
    }
  }

  async function saveCorrection(correctedFields, agentResult = null) {
    await submitAiCorrection({ doc, correctedFields, agentResult });
    setDoc((prev) => {
      if (!agentResult) {
        return { ...prev, ai: { ...prev.ai, result: { ...prev.ai?.result, ...correctedFields }, summary: correctedFields.summary || prev.ai?.summary } };
      }
      return {
        ...prev,
        agentResults: (prev.agentResults || []).map((r) => (r === agentResult ? { ...r, result: { ...(r.result || {}), ...correctedFields } } : r)),
      };
    });
  }

  if (loading) return <Loading text="Loading document…" />;
  if (!doc) return <><ErrorText>{error}</ErrorText><p className="muted">Document not found.</p></>;

  return (
    <>
      {/* An absolute path, not "..". The documents list is at
          /projects/:projectId - there is no /projects/:projectId/documents
          route - so a path-relative ".." from this page resolved to a URL
          matching nothing and fell through to the catch-all, dumping the
          user on the all-projects list instead of one level up. */}
      {/* Arriving from a citation and arriving from the list want different
          exits. Following a source is a detour from reading an answer, so the
          way back is to the answer - which is still there, the thread having
          survived the trip. */}
      {cameFromChat
        ? <Link to={`/projects/${encodeURIComponent(projectId)}/chat`} className="breadcrumb">← Back to chat</Link>
        : <Link to={`/projects/${encodeURIComponent(projectId)}`} className="breadcrumb">← Documents</Link>}
      <h2 style={{ marginBottom: 2 }}>{doc.fileName}</h2>
      <p className="sub">
        {[doc.uploadType, doc.package, ...(doc.tiers || [])].filter(Boolean).join(' · ') || 'Project Home'}
        {' · '}{formatDate(doc.uploadedAt)}
      </p>
      <ErrorText>{error}</ErrorText>

      {/* Said in full here, because this is where somebody lands after
          noticing the badge on the list and wanting to know what it means.
          The row has eleven characters to work with; this has room to say
          what happened and what fixes it.

          Deliberately above the preview: the file renders perfectly, which is
          exactly why nothing else on this page would tell them anything is
          wrong. */}
      {doc.indexed && doc.indexed.searchable === false && (
        <div className="card" style={{ marginBottom: 12 }}>
          <p style={{ marginTop: 0 }}><strong>This document is not searchable.</strong></p>
          <p className="muted" style={{ marginTop: 0 }}>
            The file is stored safely and you can open it, but its text never reached the search
            index — so questions will not find it, and the app will answer as though it is not here.
            {doc.indexed.reason ? ` Reason given: ${doc.indexed.reason}.` : ''}
          </p>
          {reindexNote
            ? <p className={reindexNote.ok ? 'saved' : 'muted'} style={{ marginTop: 0, marginBottom: 0 }}>{reindexNote.text}</p>
            : (
              <Button compact onClick={handleReindex} disabled={reindexing}>
                {reindexing ? 'Re-indexing…' : 'Re-index this document'}
              </Button>
            )}
          <p className="muted" style={{ marginTop: 8, marginBottom: 0, fontSize: '0.85em' }}>
            Re-indexing reads the stored file again. It does not change the file or create a
            duplicate, which is what uploading it a second time would do.
          </p>
        </div>
      )}

      {/* When it IS searchable. Deliberately quiet - a line, not a card -
          because nothing is wrong here and the page should not imply it is.
          It earns its place by saying WHEN the text was read, which is the
          one fact that explains a document that is indexed and still answers
          as though half of it is missing: it was read before the extractor
          could do what it can do now. A date is checkable; "try re-indexing
          it" is a guess. */}
      {doc.indexed && doc.indexed.searchable !== false && (
        <p className="muted" style={{ marginTop: 0, marginBottom: 12, fontSize: '0.85em' }}>
          Text read for search{doc.indexed.at ? ` ${formatDate(doc.indexed.at)}` : ''}
          {doc.indexed.chars ? ` · ${doc.indexed.chars.toLocaleString()} characters` : ''}
          {/* The comment count, which is the fact that explains a document
              answering as though half of it is missing. null means nobody
              counted - it was read before the extractor could see comments -
              and that is deliberately not shown as "0 comments", because
              "none were written on it" and "none were looked for" send you
              after two completely different problems. */}
          {doc.indexed.comments == null
            ? ''
            : ` · ${doc.indexed.comments} comment${doc.indexed.comments === 1 ? '' : 's'} read`}
          {' · '}
          {reindexNote
            ? <span className={reindexNote.ok ? 'saved' : undefined}>{reindexNote.text}</span>
            : (
              <button
                type="button"
                className="linkish"
                onClick={handleReindex}
                disabled={reindexing}
                style={{ background: 'none', border: 0, padding: 0, font: 'inherit', color: 'inherit', textDecoration: 'underline', cursor: 'pointer' }}
              >
                {reindexing ? 'Re-reading…' : 'Read it again'}
              </button>
            )}
        </p>
      )}

      {previewUrl && (
        <div className="preview">
          {isImage(doc)
            ? <img src={previewUrl} alt={doc.fileName} />
            : <iframe
                src={Number.isFinite(wantedPage) && wantedPage > 0 ? `${previewUrl}#page=${wantedPage}` : previewUrl}
                title={doc.fileName}
              />}
        </div>
      )}
      <div className="row" style={{ marginBottom: 14 }}>
        <Button compact secondary onClick={() => downloadBlob(doc.blobName, doc.fileName)}>Download</Button>
      </div>

      <ProcorePushCard doc={doc} project={project} />
      <ProcoreSendToDocumentsCard doc={doc} project={project} />

      <AiResponseCard
        title="AI response"
        status={doc.ai?.status}
        result={doc.ai?.result || {}}
        emptyText="No AI analysis yet for this document."
        pending={(doc.agentRuns || []).some((r) => String(r.agentId || r.agentName || '').toLowerCase().replace(/\s+/g, '') === 'photoanalysis')}
        onSaveCorrection={(fields) => saveCorrection(fields, null)}
      />

      {/* Agent responses stack below the base analysis rather than replacing
          it, so running a second agent adds a view instead of overwriting. */}
      {agentResults.map((agentResult, index) => (
        <AiResponseCard
          key={`${agentResult.agentId || agentResult.agentName || 'agent'}-${index}`}
          title={agentResult.agentName || agentResult.label || 'Agent response'}
          subtitle={[agentResult.model, formatDate(agentResult.completedAt || agentResult.updatedAt)].filter(Boolean).join(' · ')}
          status={agentResult.status}
          result={agentResult.result || agentResult.response || {}}
          emptyText={agentResult.error || 'This agent returned no structured fields.'}
          onSaveCorrection={(fields) => saveCorrection(fields, agentResult)}
        />
      ))}

      {pendingRuns.map((run) => (
        <div className="card" key={run.agentId || run.agentName} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span className="spinner" />
          <span className="muted" style={{ fontWeight: 700 }}>{run.agentName || 'Agent'} is running…</span>
        </div>
      ))}

      <Card
        title="Run an agent"
        hint={!agentsLoaded
          ? 'Loading agents…'
          : runnableAgents.length
            ? `Agents set up for ${doc.uploadType || 'this upload type'}. The response is added below, alongside the existing ones.`
            : `No agents are set up for ${doc.uploadType || 'this upload type'}. Create one on the Agents tab and set its upload category.`}
      >
        <Select
          label="Agent"
          value={selectedAgentId}
          onChange={setSelectedAgentId}
          options={runnableAgents.map((a) => ({ id: a.id, label: a.label, sublabel: a.description }))}
          placeholder={!agentsLoaded ? 'Loading…' : runnableAgents.length ? 'Choose an agent…' : 'No agents for this upload type'}
          disabled={!runnableAgents.length || running}
        />
        {/* Said before the run, not after it. Without a provider connected the
            job queues, fails inside a worker, and comes back as a blank result
            - which reads as the app being broken rather than as something the
            customer has yet to set up. */}
        {hasAiConnection === false && (
          <p className="sub" style={{ marginTop: 8 }}>
            No AI provider is connected yet, so agents cannot run. Add one under{' '}
            <Link to="/ai-connections">AI connections</Link>, then come back.
          </p>
        )}
        {agentNotice && <p className="saved">{agentNotice}</p>}
        <ErrorText>{agentError}</ErrorText>
        <Button onClick={handleRunAgent} disabled={!selectedAgentId || running || hasAiConnection === false}>
          {running ? 'Queueing…' : 'Run agent'}
        </Button>
      </Card>
    </>
  );
}
